export type AgentMentionQuery = {
  query: string;
  start: number;
  end: number;
};

const MENTION_QUERY_PATTERN = /(?:^|\s)@([A-Za-z0-9]*)$/;

/**
 * Finds an in-progress `@Nickname` mention query ending exactly at `caret`,
 * so the caller can drive an autocomplete popover from a multi-line textarea
 * where the caret isn't necessarily at the end of the value.
 */
export function findAgentMentionQuery(value: string, caret: number): AgentMentionQuery | null {
  const upToCaret = value.slice(0, caret);
  const match = upToCaret.match(MENTION_QUERY_PATTERN);
  if (!match) return null;

  const query = match[1];
  const end = caret;
  const start = end - query.length - 1; // include the "@"

  return { query, start, end };
}

export type MentionableAgent = {
  id: string;
  nickname: string;
};

/** Case-insensitive prefix match of the typed query against stored nicknames. */
export function filterAgentsByMentionQuery<T extends MentionableAgent>(
  agents: T[],
  query: string
): T[] {
  const normalizedQuery = query.toLowerCase();
  return agents.filter((agent) => agent.nickname.toLowerCase().startsWith(normalizedQuery));
}

export type AgentMentionTokenRange = {
  start: number;
  end: number;
};

/**
 * Locates the first `@Nickname` occurrence in `text`. Shared by the input
 * highlight (which styles the token in place) and `stripAgentMentionToken`
 * (which removes it before send) so both always agree on the same token.
 */
export function findAgentMentionTokenRange(
  text: string,
  mention: { nickname: string }
): AgentMentionTokenRange | null {
  const token = `@${mention.nickname}`;
  const index = text.indexOf(token);
  if (index === -1) return null;
  return { start: index, end: index + token.length };
}

/**
 * Removes the first `@Nickname` occurrence (plus one trailing space, if any)
 * from a message before it's sent — routing syntax has no use to the model
 * and would replay confusingly into later turns. Attribution is shown via a
 * reply badge instead.
 */
export function stripAgentMentionToken(text: string, mention: { nickname: string }): string {
  const range = findAgentMentionTokenRange(text, mention);
  if (!range) return text;

  const before = text.slice(0, range.start);
  let after = text.slice(range.end);
  if (after.startsWith(" ")) {
    after = after.slice(1);
  }
  return `${before}${after}`;
}
